import { motion } from "framer-motion";
import { Send, CheckCircle2, XCircle, Mail, AlertTriangle, RefreshCw } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useState } from "react";
import { Link } from "react-router-dom";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import { EmailTabNav } from "@/components/admin/email/EmailTabNav";
import KpiGrid from "@/components/admin/KpiGrid";
import { Button } from "@/components/ui/button";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { EMAIL_STATUS_CONFIG } from "@/types/email";
import type { EmailDashboardMetrics } from "@/types/email";

type PeriodDays = 7 | 30 | 90;

export default function AdminEmails() {
  const [days, setDays] = useState<PeriodDays>(30);

  const { data: metrics, isLoading, refetch } = useQuery({
    queryKey: ["email-dashboard-metrics", days],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("email_dashboard_metrics", {
        p_days: days,
      });
      if (error) throw error;
      return data as unknown as EmailDashboardMetrics;
    },
    staleTime: 5 * 60 * 1000,
  });

  // Últimos 10 envios
  const { data: recentEmails } = useQuery({
    queryKey: ["email-recent-sends"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("email_logs")
        .select("id, template_key, recipient_email, status, created_at, triggered_by")
        .order("created_at", { ascending: false })
        .limit(10);
      if (error) throw error;
      return data;
    },
  });

  // Origin breakdown for KPI (EM17 AC5)
  const { data: originBreakdown } = useQuery({
    queryKey: ["email-origin-breakdown", days],
    queryFn: async () => {
      const since = new Date(Date.now() - days * 86400000).toISOString();
      const { data, error } = await supabase
        .from("email_logs")
        .select("triggered_by")
        .gte("created_at", since);
      if (error) throw error;
      const counts = { system: 0, admin: 0, cron: 0 };
      (data ?? []).forEach((r) => {
        if (r.triggered_by === "system") counts.system++;
        else if (r.triggered_by.startsWith("admin:") || r.triggered_by.startsWith("retry:")) counts.admin++;
        else if (r.triggered_by.startsWith("cron:")) counts.cron++;
        else counts.system++;
      });
      const total = counts.system + counts.admin + counts.cron;
      return { ...counts, total };
    },
    staleTime: 5 * 60 * 1000,
  });

  // Template display names for the top templates list
  const { data: templateNames } = useQuery({
    queryKey: ["email-template-names"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("email_templates_config")
        .select("template_key, display_name");
      if (error) throw error;
      return Object.fromEntries(
        (data ?? []).map((t) => [t.template_key, t.display_name])
      );
    },
    staleTime: 10 * 60 * 1000,
  });

  const kpis = [
    {
      icon: Send,
      label: `Enviados (${days}d)`,
      value: metrics?.total_sent ?? 0,
      color: "bg-blue-100 text-blue-600",
    },
    {
      icon: CheckCircle2,
      label: "Taxa de Sucesso",
      value: metrics?.success_rate ?? 0,
      sub: "%",
      color: "bg-emerald-100 text-emerald-600",
    },
    {
      icon: XCircle,
      label: `Falhas (${days}d)`,
      value: metrics?.total_failed ?? 0,
      color: "bg-rose-100 text-rose-600",
    },
    {
      icon: Mail,
      label: "Hoje",
      value: metrics?.total_today ?? 0,
      color: "bg-purple-100 text-purple-600",
    },
  ];

  const maxTemplateCount = Math.max(
    ...(metrics?.by_template?.map((t) => t.count) ?? [1])
  );

  return (
    <div className="p-6 lg:p-8 max-w-7xl">
      <EmailTabNav />

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Central de Emails</h1>
          <p className="text-sm text-muted-foreground">
            Gerencie templates, monitore envios e acompanhe métricas
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => refetch()}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Atualizar
        </Button>
      </div>

      {/* Banner de alerta falhas 24h */}
      {metrics && metrics.failures_24h > 0 && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-red-50 border border-red-200 rounded-2xl p-4 mb-6 flex items-center gap-3"
        >
          <AlertTriangle className="w-5 h-5 text-red-600 shrink-0" />
          <p className="text-sm font-medium text-red-800 flex-1">
            {metrics.failures_24h} email(s) falharam nas últimas 24h
          </p>
          <Link
            to="/admin/emails/log?status=failed"
            className="text-sm font-medium text-red-600 hover:underline whitespace-nowrap"
          >
            Ver falhas →
          </Link>
        </motion.div>
      )}

      {/* KPI Cards */}
      {isLoading ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="bg-card rounded-2xl p-5 border border-border animate-pulse h-28" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {kpis.map((kpi, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.05 * i }}
              className="bg-card rounded-2xl p-5 border border-border"
            >
              <div className={`w-10 h-10 rounded-xl ${kpi.color} flex items-center justify-center mb-3`}>
                <kpi.icon className="w-5 h-5" />
              </div>
              <p className="text-3xl font-bold text-foreground">
                {kpi.value.toLocaleString("pt-BR")}
                {kpi.sub && <span className="text-lg">{kpi.sub}</span>}
              </p>
              <p className="text-sm text-muted-foreground">{kpi.label}</p>
            </motion.div>
          ))}
        </div>
      )}

      {/* Period Filter */}
      <div className="flex justify-end mb-6">
        <ToggleGroup
          type="single"
          value={String(days)}
          onValueChange={(v) => v && setDays(Number(v) as PeriodDays)}
          className="bg-secondary rounded-lg p-1"
        >
          {[7, 30, 90].map((d) => (
            <ToggleGroupItem
              key={d}
              value={String(d)}
              className="text-xs font-medium px-3 data-[state=on]:bg-primary data-[state=on]:text-primary-foreground rounded-md"
            >
              {d} dias
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
      </div>

      {/* Volume Chart */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-card rounded-2xl p-5 border border-border mb-8"
      >
        <h3 className="text-lg font-semibold text-foreground mb-4">Volume Diário</h3>
        {metrics?.daily_volume && metrics.daily_volume.length > 0 ? (
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={metrics.daily_volume}>
              <defs>
                <linearGradient id="gradSent" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="gradFailed" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="hsl(var(--destructive))" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="hsl(var(--destructive))" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 11 }}
                tickFormatter={(d) => {
                  const parts = d.split("-");
                  return `${parts[2]}/${parts[1]}`;
                }}
              />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Legend />
              <Area
                type="monotone"
                dataKey="sent"
                name="Enviados"
                stroke="hsl(var(--primary))"
                fill="url(#gradSent)"
                strokeWidth={2}
              />
              <Area
                type="monotone"
                dataKey="failed"
                name="Falhas"
                stroke="hsl(var(--destructive))"
                fill="url(#gradFailed)"
                strokeWidth={2}
              />
            </AreaChart>
          </ResponsiveContainer>
        ) : (
          <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
            <Mail className="w-7 h-7 mb-3" />
            <p className="font-medium">Nenhum dado de volume</p>
            <p className="text-xs">Os dados aparecerão conforme emails forem disparados</p>
          </div>
        )}
      </motion.div>

      {/* Bottom Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Top Templates */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-card rounded-2xl p-5 border border-border"
        >
          <h3 className="text-lg font-semibold text-foreground mb-4">Top Templates</h3>
          {metrics?.by_template && metrics.by_template.length > 0 ? (
            <div className="space-y-4">
              {metrics.by_template.map((t) => (
                <Link
                  key={t.template_key}
                  to={`/admin/emails/templates/${t.template_key}`}
                  className="block group"
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium group-hover:text-primary transition-colors truncate mr-2">
                      {templateNames?.[t.template_key] ?? t.template_key}
                    </span>
                    <span className="text-sm font-bold tabular-nums text-foreground">
                      {t.count.toLocaleString("pt-BR")}
                    </span>
                  </div>
                  <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                    <div
                      className="h-full bg-primary rounded-full transition-all"
                      style={{ width: `${(t.count / maxTemplateCount) * 100}%` }}
                    />
                  </div>
                  {t.failed > 0 && (
                    <p className="text-xs text-red-600 mt-1">{t.failed} falha(s)</p>
                  )}
                </Link>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">Nenhum template utilizado no período</p>
          )}
        </motion.div>

        {/* Últimos Envios */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35 }}
          className="lg:col-span-2 bg-card rounded-2xl p-5 border border-border"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-foreground">Últimos Envios</h3>
            <Link
              to="/admin/emails/log"
              className="text-sm font-medium text-primary hover:underline"
            >
              Ver todos →
            </Link>
          </div>
          {recentEmails && recentEmails.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="text-xs font-medium text-muted-foreground uppercase border-b border-border">
                    <th className="pb-2">Hora</th>
                    <th className="pb-2">Template</th>
                    <th className="pb-2">Destinatário</th>
                    <th className="pb-2">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {recentEmails.map((email) => {
                    const statusConfig = EMAIL_STATUS_CONFIG[email.status as keyof typeof EMAIL_STATUS_CONFIG];
                    return (
                      <tr key={email.id} className="hover:bg-muted/30">
                        <td className="py-2.5 text-xs text-muted-foreground whitespace-nowrap">
                          {new Date(email.created_at).toLocaleString("pt-BR", {
                            day: "2-digit",
                            month: "2-digit",
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </td>
                        <td className="py-2.5 text-sm">
                          {templateNames?.[email.template_key] ?? email.template_key}
                        </td>
                        <td className="py-2.5 text-sm text-muted-foreground truncate max-w-[180px]">
                          {email.recipient_email}
                        </td>
                        <td className="py-2.5">
                          {statusConfig && (
                            <span className={`inline-flex items-center gap-1 text-[11px] font-medium px-2.5 py-1 rounded-full ${statusConfig.color}`}>
                              {email.status === "failed" && <AlertTriangle className="w-3 h-3" />}
                              {statusConfig.label}
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
              <Mail className="w-7 h-7 mb-3" />
              <p className="font-medium">Nenhum email enviado ainda</p>
              <p className="text-xs">Os dados aparecerão conforme emails forem disparados</p>
            </div>
          )}
        </motion.div>
      </div>

      {/* Origin breakdown (EM17 AC5) */}
      {originBreakdown && originBreakdown.total > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-card rounded-2xl p-5 border border-border mt-6"
        >
          <h3 className="text-sm font-semibold text-foreground mb-3">Origem dos envios ({days}d)</h3>
          <div className="flex gap-6">
            {[
              { label: "Sistema", value: originBreakdown.system, color: "bg-blue-500" },
              { label: "Admin", value: originBreakdown.admin, color: "bg-violet-500" },
              { label: "Cron", value: originBreakdown.cron, color: "bg-amber-500" },
            ].map((o) => (
              <div key={o.label} className="flex items-center gap-2">
                <div className={`w-3 h-3 rounded-full ${o.color}`} />
                <span className="text-sm text-muted-foreground">{o.label}</span>
                <span className="text-sm font-bold">{o.value}</span>
                <span className="text-xs text-muted-foreground">
                  ({originBreakdown.total > 0 ? Math.round((o.value / originBreakdown.total) * 100) : 0}%)
                </span>
              </div>
            ))}
          </div>
        </motion.div>
      )}
    </div>
  );
}
